import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  Wallet, 
  Receipt, 
  CreditCard, 
  FileText, 
  Search, 
  Download, 
  ChevronDown, 
  ChevronUp,
  CheckCircle,
  Clock,
  AlertCircle,
  Banknote,
  Building2,
  Calendar,
  Filter,
  Plus,
  X,
  Bell,
  Send,
  Settings,
  Mail,
  History,
  Users,
  Shield,
  ListChecks,
  Ban,
  CalendarClock,
  Smartphone,
  MessageCircle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CommissionLedgerTab() {
  const [activeView, setActiveView] = useState('balances'); // balances, ledger, statement
  const [balances, setBalances] = useState([]);
  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [selectedOperator, setSelectedOperator] = useState(null);
  const [operatorBalance, setOperatorBalance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Filters
  const [filters, setFilters] = useState({
    operatorId: '',
    status: 'all',
    dateFrom: '',
    dateTo: ''
  });
  
  // Modals
  const [showRecordPayment, setShowRecordPayment] = useState(false);
  const [showAdjustment, setShowAdjustment] = useState(false);
  const [showStatement, setShowStatement] = useState(false);
  const [statement, setStatement] = useState(null);
  
  // Payment form
  const [paymentForm, setPaymentForm] = useState({
    operatorId: '',
    amount: '',
    paymentMethod: 'bank_transfer',
    reference: '',
    notes: ''
  });
  
  // Adjustment form
  const [adjustmentForm, setAdjustmentForm] = useState({
    operatorId: '',
    amount: '',
    reason: ''
  });

  // Notification settings
  // Notification settings
  const [notificationSettings, setNotificationSettings] = useState({
    enabled: false,
    thresholdAmount: 500,
    reminderFrequency: 'weekly',
    includeStatement: true,
    emailSubject: 'Tekstar: Commission Payment Reminder',
    smsEnabled: false,
    smsMessage: 'Tekstar: Your commission balance of K{amount} is due. Please settle at your earliest convenience.',
    whatsappEnabled: false,
    whatsappMessage: '📢 *Tekstar Commission Reminder*\n\nYour outstanding commission balance is *K{amount}*.\n\nPlease settle at your earliest convenience.\n\n💳 Payment options: Bank Transfer, Mobile Money, or Cash'
  });
  const [showNotificationSettings, setShowNotificationSettings] = useState(false);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [notificationHistory, setNotificationHistory] = useState([]);
  const [showNotificationHistory, setShowNotificationHistory] = useState(false);
  const [smsConfigured, setSmsConfigured] = useState(false);
  const [whatsappConfigured, setWhatsappConfigured] = useState(false);

  // Bulk payment
  const [showBulkPayment, setShowBulkPayment] = useState(false);
  const [bulkPayments, setBulkPayments] = useState([]);
  const [bulkPaymentMethod, setBulkPaymentMethod] = useState('bank_transfer');
  const [bulkPaymentNotes, setBulkPaymentNotes] = useState('');

  // Balance limit
  const [balanceLimitSettings, setBalanceLimitSettings] = useState({
    enabled: false,
    limitAmount: 1000,
    blockNewBookings: true,
    warningThreshold: 800,
    gracePeriodDays: 7
  });
  const [showBalanceLimitSettings, setShowBalanceLimitSettings] = useState(false);
  const [operatorsOverLimit, setOperatorsOverLimit] = useState([]);

  // Payment plans
  const [paymentPlans, setPaymentPlans] = useState([]);
  const [showPaymentPlans, setShowPaymentPlans] = useState(false);
  const [showCreatePlan, setShowCreatePlan] = useState(false);
  const [newPlan, setNewPlan] = useState({
    operatorId: '',
    totalAmount: '',
    numInstallments: 3,
    frequency: 'monthly',
    notes: ''
  });
  const [selectedPlan, setSelectedPlan] = useState(null);

  // Payment plan reminders
  const [planReminderSettings, setPlanReminderSettings] = useState({
    enabled: false,
    daysBeforeDue: 3,
    sendOverdueReminders: true,
    overdueReminderFrequency: 'daily',
    emailSubjectUpcoming: 'Tekstar: Payment Installment Due Soon',
    emailSubjectOverdue: 'Tekstar: Overdue Payment Installment'
  });
  const [showPlanReminderSettings, setShowPlanReminderSettings] = useState(false);
  const [sendingPlanReminders, setSendingPlanReminders] = useState(false);
  const [planReminderHistory, setPlanReminderHistory] = useState([]);
  const [showPlanReminderHistory, setShowPlanReminderHistory] = useState(false);

  const token = localStorage.getItem('tekstar_token');
  const headers = { Authorization: `Bearer ${token}` };

  // Fetch all operator balances
  const fetchBalances = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/bus/commission/balances`, { headers });
      setBalances(res.data.balances || []);
      setError(null);
    } catch (err) {
      setError('Failed to fetch balances');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch ledger entries
  const fetchLedger = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.operatorId) params.append('operator_id', filters.operatorId);
      if (filters.status !== 'all') params.append('status', filters.status);
      if (filters.dateFrom) params.append('date_from', filters.dateFrom);
      if (filters.dateTo) params.append('date_to', filters.dateTo);
      
      const res = await axios.get(`${API}/api/admin/bus/commission/ledger?${params}`, { headers });
      setLedgerEntries(res.data.entries || []);
      setError(null);
    } catch (err) {
      setError('Failed to fetch ledger');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Fetch specific operator balance
  const fetchOperatorBalance = async (operatorId) => {
    try {
      const res = await axios.get(`${API}/api/admin/bus/commission/operator/${operatorId}/balance`, { headers });
      setOperatorBalance(res.data);
      setSelectedOperator(operatorId);
    } catch (err) {
      console.error(err);
    }
  };

  // Generate statement
  const generateStatement = async (operatorId) => {
    try {
      const params = new URLSearchParams();
      if (filters.dateFrom) params.append('date_from', filters.dateFrom);
      if (filters.dateTo) params.append('date_to', filters.dateTo);
      
      const res = await axios.get(`${API}/api/admin/bus/commission/statement/${operatorId}?${params}`, { headers });
      setStatement(res.data.statement);
      setShowStatement(true);
    } catch (err) {
      console.error(err);
      alert('Failed to generate statement');
    }
  };

  // Download statement as PDF
  const downloadStatementPDF = async () => {
    if (!statement?.operator?.id) return;
    
    try {
      const params = new URLSearchParams();
      if (filters.dateFrom) params.append('date_from', filters.dateFrom);
      if (filters.dateTo) params.append('date_to', filters.dateTo);
      
      const response = await axios.get(
        `${API}/api/admin/bus/commission/statement/${statement.operator.id}/pdf?${params}`,
        { 
          headers,
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Statement_${statement.operator.name?.replace(/\s+/g, '_')}_${statement.period?.from}_to_${statement.period?.to}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      alert('Failed to download statement PDF');
    }
  };

  // Record payment
  const handleRecordPayment = async (e) => {
    e.preventDefault();
    try {
      const params = new URLSearchParams({
        operator_id: paymentForm.operatorId,
        amount: paymentForm.amount,
        payment_method: paymentForm.paymentMethod
      });
      if (paymentForm.reference) params.append('reference', paymentForm.reference);
      if (paymentForm.notes) params.append('notes', paymentForm.notes);
      
      await axios.post(`${API}/api/admin/bus/commission/record-payment?${params}`, {}, { headers });
      
      setShowRecordPayment(false);
      setPaymentForm({ operatorId: '', amount: '', paymentMethod: 'bank_transfer', reference: '', notes: '' });
      fetchBalances();
      fetchLedger();
      alert('Payment recorded successfully');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to record payment');
    }
  };

  // Create adjustment
  const handleCreateAdjustment = async (e) => {
    e.preventDefault();
    try {
      const params = new URLSearchParams({
        operator_id: adjustmentForm.operatorId,
        amount: adjustmentForm.amount,
        reason: adjustmentForm.reason
      });
      
      await axios.post(`${API}/api/admin/bus/commission/adjustment?${params}`, {}, { headers });
      
      setShowAdjustment(false);
      setAdjustmentForm({ operatorId: '', amount: '', reason: '' });
      fetchBalances();
      fetchLedger();
      alert('Adjustment created successfully');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to create adjustment');
    }
  };

  // Fetch notification settings
  const fetchNotificationSettings = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/bus/commission/notification-settings`, { headers });
      if (res.data.settings) {
        setNotificationSettings({
          enabled: res.data.settings.enabled || false,
          thresholdAmount: res.data.settings.thresholdAmount || 500,
          reminderFrequency: res.data.settings.reminderFrequency || 'weekly',
          includeStatement: res.data.settings.includeStatement !== false,
          emailSubject: res.data.settings.emailSubject || 'Tekstar: Commission Payment Reminder',
          smsEnabled: res.data.settings.smsEnabled || false,
          smsMessage: res.data.settings.smsMessage || 'Tekstar: Your commission balance of K{amount} is due. Please settle at your earliest convenience.',
          whatsappEnabled: res.data.settings.whatsappEnabled || false,
          whatsappMessage: res.data.settings.whatsappMessage || '📢 *Tekstar Commission Reminder*\n\nYour outstanding commission balance is *K{amount}*.\n\nPlease settle at your earliest convenience.\n\n💳 Payment options: Bank Transfer, Mobile Money, or Cash'
        });
      }
      setSmsConfigured(res.data.smsConfigured || false);
      setWhatsappConfigured(res.data.whatsappConfigured || false);
    } catch (err) {
      console.error('Failed to fetch notification settings:', err);
    }
  };

  // Save notification settings
  const handleSaveNotificationSettings = async () => {
    try {
      await axios.put(`${API}/api/admin/bus/commission/notification-settings`, {
        enabled: notificationSettings.enabled,
        threshold_amount: notificationSettings.thresholdAmount,
        reminder_frequency: notificationSettings.reminderFrequency,
        include_statement: notificationSettings.includeStatement,
        email_subject: notificationSettings.emailSubject,
        sms_enabled: notificationSettings.smsEnabled,
        sms_message: notificationSettings.smsMessage,
        whatsapp_enabled: notificationSettings.whatsappEnabled,
        whatsapp_message: notificationSettings.whatsappMessage
      }, { headers });
      
      alert('Notification settings saved successfully');
      setShowNotificationSettings(false);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save settings');
    }
  };

  // Send reminder emails and SMS
  const handleSendReminders = async (testMode = false) => {
    setSendingReminders(true);
    try {
      const res = await axios.post(
        `${API}/api/admin/bus/commission/send-reminders?test_mode=${testMode}`,
        {},
        { headers }
      );
      
      if (res.data.sent > 0) {
        alert(`${testMode ? '[TEST] ' : ''}${res.data.message}`);
      } else {
        alert(res.data.message || 'No reminders to send');
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to send reminders');
    } finally {
      setSendingReminders(false);
    }
  };

  // Fetch notification history
  const fetchNotificationHistory = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/bus/commission/notification-history`, { headers });
      setNotificationHistory(res.data.notifications || []);
      setShowNotificationHistory(true);
    } catch (err) {
      console.error('Failed to fetch notification history:', err);
    }
  };

  // Bulk Payment Functions
  const initBulkPayment = () => {
    // Initialize bulk payments with operators who have outstanding balance
    const operatorsWithBalance = balances.filter(b => b.balance > 0);
    setBulkPayments(operatorsWithBalance.map(op => ({
      operatorId: op.operatorId,
      operatorName: op.operatorName,
      balance: op.balance,
      amount: '',
      selected: false,
      reference: ''
    })));
    setShowBulkPayment(true);
  };

  const handleBulkPaymentSubmit = async () => {
    const selectedPayments = bulkPayments.filter(p => p.selected && p.amount > 0);
    if (selectedPayments.length === 0) {
      alert('Please select operators and enter payment amounts');
      return;
    }

    try {
      const res = await axios.post(`${API}/api/admin/bus/commission/bulk-payment`, {
        payments: selectedPayments.map(p => ({
          operator_id: p.operatorId,
          amount: parseFloat(p.amount),
          reference: p.reference
        })),
        payment_method: bulkPaymentMethod,
        notes: bulkPaymentNotes
      }, { headers });

      alert(res.data.message);
      setShowBulkPayment(false);
      setBulkPayments([]);
      fetchBalances();
      fetchLedger();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to process bulk payment');
    }
  };

  // Balance Limit Functions
  const fetchBalanceLimitSettings = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/bus/commission/balance-limit`, { headers });
      if (res.data.settings) {
        setBalanceLimitSettings({
          enabled: res.data.settings.enabled || false,
          limitAmount: res.data.settings.limitAmount || 1000,
          blockNewBookings: res.data.settings.blockNewBookings !== false,
          warningThreshold: res.data.settings.warningThreshold || 800,
          gracePeriodDays: res.data.settings.gracePeriodDays || 7
        });
      }
      setOperatorsOverLimit(res.data.operatorsOverLimit || []);
    } catch (err) {
      console.error('Failed to fetch balance limit settings:', err);
    }
  };

  const handleSaveBalanceLimitSettings = async () => {
    try {
      await axios.put(`${API}/api/admin/bus/commission/balance-limit`, {
        enabled: balanceLimitSettings.enabled,
        limit_amount: balanceLimitSettings.limitAmount,
        block_new_bookings: balanceLimitSettings.blockNewBookings,
        warning_threshold: balanceLimitSettings.warningThreshold,
        grace_period_days: balanceLimitSettings.gracePeriodDays
      }, { headers });

      alert('Balance limit settings saved');
      setShowBalanceLimitSettings(false);
      fetchBalanceLimitSettings();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save settings');
    }
  };

  // Payment Plans Functions
  const fetchPaymentPlans = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/bus/commission/payment-plans`, { headers });
      setPaymentPlans(res.data.plans || []);
    } catch (err) {
      console.error('Failed to fetch payment plans:', err);
    }
  };

  const handleCreatePaymentPlan = async () => {
    if (!newPlan.operatorId || !newPlan.totalAmount) {
      alert('Please select an operator and enter total amount');
      return;
    }

    try {
      const res = await axios.post(`${API}/api/admin/bus/commission/payment-plans`, {
        operator_id: newPlan.operatorId,
        total_amount: parseFloat(newPlan.totalAmount),
        num_installments: newPlan.numInstallments,
        frequency: newPlan.frequency,
        notes: newPlan.notes
      }, { headers });

      // Close the create modal
      setShowCreatePlan(false);
      setNewPlan({ operatorId: '', totalAmount: '', numInstallments: 3, frequency: 'monthly', notes: '' });
      
      // Refresh plans list
      fetchPaymentPlans();
      
      // Show success message
      alert(`✅ ${res.data.message}\n\nClick "Done" to return to Settlement.`);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to create payment plan');
    }
  };

  const handlePayInstallment = async (planId, installmentNumber, amount) => {
    try {
      const res = await axios.post(
        `${API}/api/admin/bus/commission/payment-plans/${planId}/pay-installment?installment_number=${installmentNumber}&amount=${amount}`,
        {},
        { headers }
      );
      alert(res.data.message);
      fetchPaymentPlans();
      setSelectedPlan(null);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to record payment');
    }
  };

  const handleCancelPlan = async (planId) => {
    if (!confirm('Are you sure you want to cancel this payment plan?')) return;

    try {
      await axios.put(`${API}/api/admin/bus/commission/payment-plans/${planId}/cancel`, {}, { headers });
      alert('Payment plan cancelled');
      fetchPaymentPlans();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to cancel plan');
    }
  };

  // Payment Plan Reminder Functions
  const fetchPlanReminderSettings = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/bus/commission/payment-plan-reminders/settings`, { headers });
      if (res.data.settings) {
        setPlanReminderSettings({
          enabled: res.data.settings.enabled || false,
          daysBeforeDue: res.data.settings.daysBeforeDue || 3,
          sendOverdueReminders: res.data.settings.sendOverdueReminders !== false,
          overdueReminderFrequency: res.data.settings.overdueReminderFrequency || 'daily',
          emailSubjectUpcoming: res.data.settings.emailSubjectUpcoming || 'Tekstar: Payment Installment Due Soon',
          emailSubjectOverdue: res.data.settings.emailSubjectOverdue || 'Tekstar: Overdue Payment Installment'
        });
      }
    } catch (err) {
      console.error('Failed to fetch plan reminder settings:', err);
    }
  };

  const handleSavePlanReminderSettings = async () => {
    try {
      await axios.put(`${API}/api/admin/bus/commission/payment-plan-reminders/settings`, {
        enabled: planReminderSettings.enabled,
        days_before_due: planReminderSettings.daysBeforeDue,
        send_overdue_reminders: planReminderSettings.sendOverdueReminders,
        overdue_reminder_frequency: planReminderSettings.overdueReminderFrequency,
        email_subject_upcoming: planReminderSettings.emailSubjectUpcoming,
        email_subject_overdue: planReminderSettings.emailSubjectOverdue
      }, { headers });
      
      alert('Payment plan reminder settings saved');
      setShowPlanReminderSettings(false);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save settings');
    }
  };

  const handleSendPlanReminders = async (testMode = false) => {
    setSendingPlanReminders(true);
    try {
      const res = await axios.post(
        `${API}/api/admin/bus/commission/payment-plan-reminders/send?test_mode=${testMode}`,
        {},
        { headers }
      );
      
      if (res.data.sent > 0) {
        alert(`${testMode ? '[TEST] ' : ''}${res.data.message}`);
      } else {
        alert(res.data.message || 'No reminders to send');
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to send reminders');
    } finally {
      setSendingPlanReminders(false);
    }
  };

  const fetchPlanReminderHistory = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/bus/commission/payment-plan-reminders/history`, { headers });
      setPlanReminderHistory(res.data.reminders || []);
      setShowPlanReminderHistory(true);
    } catch (err) {
      console.error('Failed to fetch plan reminder history:', err);
    }
  };

  useEffect(() => {
    if (activeView === 'balances') {
      fetchBalances();
    } else if (activeView === 'ledger') {
      fetchLedger();
    }
  }, [activeView, fetchBalances, fetchLedger]);

  // Load notification settings and balance limits on mount
  useEffect(() => {
    fetchNotificationSettings();
    fetchBalanceLimitSettings();
    fetchPaymentPlans();
    fetchPlanReminderSettings();
  }, []);

  // Summary stats from balances
  const totalOutstanding = balances.reduce((sum, b) => sum + Math.max(0, b.balance), 0);
  const operatorsWithDebt = balances.filter(b => b.balance > 0).length;

  return (
    <div className="space-y-6">
      {/* Header with Actions */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Commission Settlement</h2>
          <p className="text-sm text-gray-500">Track and collect commissions from operators</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setShowRecordPayment(true)}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            data-testid="record-payment-btn"
          >
            <Plus className="w-4 h-4" />
            Record Payment
          </button>
          <button
            onClick={() => setShowAdjustment(true)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
            data-testid="create-adjustment-btn"
          >
            <FileText className="w-4 h-4" />
            Adjustment
          </button>
          <button
            onClick={() => handleSendReminders(false)}
            disabled={sendingReminders}
            className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50"
            data-testid="send-reminders-btn"
          >
            <Send className="w-4 h-4" />
            {sendingReminders ? 'Sending...' : 'Send Reminders'}
          </button>
          <button
            onClick={() => setShowNotificationSettings(true)}
            className="flex items-center gap-2 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            data-testid="notification-settings-btn"
          >
            <Bell className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Secondary Actions Row */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={initBulkPayment}
          className="flex items-center gap-2 px-3 py-1.5 text-sm bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200"
          data-testid="bulk-payment-btn"
        >
          <Users className="w-4 h-4" />
          Bulk Payment
        </button>
        <button
          onClick={() => { setShowPaymentPlans(true); fetchPaymentPlans(); }}
          className="flex items-center gap-2 px-3 py-1.5 text-sm bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200"
          data-testid="payment-plans-btn"
        >
          <CalendarClock className="w-4 h-4" />
          Payment Plans ({paymentPlans.filter(p => p.status === 'active').length})
        </button>
        <button
          onClick={() => { setShowBalanceLimitSettings(true); fetchBalanceLimitSettings(); }}
          className="flex items-center gap-2 px-3 py-1.5 text-sm bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
          data-testid="balance-limit-btn"
        >
          <Shield className="w-4 h-4" />
          Balance Limit
          {balanceLimitSettings.enabled && operatorsOverLimit.length > 0 && (
            <span className="bg-red-500 text-white text-xs px-1.5 rounded-full">{operatorsOverLimit.length}</span>
          )}
        </button>
      </div>

      {/* Notification Status Banner */}
      {(notificationSettings.enabled || notificationSettings.smsEnabled || notificationSettings.whatsappEnabled) && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center justify-between">
          <div className="flex items-center gap-2 flex-wrap">
            <Bell className="w-4 h-4 text-amber-600" />
            <span className="text-sm text-amber-800">
              Auto-reminders enabled: Operators with balance over <strong>K{notificationSettings.thresholdAmount.toLocaleString()}</strong> will receive {notificationSettings.reminderFrequency} reminders via
              {[
                notificationSettings.enabled && 'Email',
                notificationSettings.smsEnabled && 'SMS',
                notificationSettings.whatsappEnabled && 'WhatsApp'
              ].filter(Boolean).join(', ')}
            </span>
            {notificationSettings.smsEnabled && (
              <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                <Smartphone className="w-3 h-3" /> SMS
              </span>
            )}
            {notificationSettings.whatsappEnabled && (
              <span className="bg-emerald-100 text-emerald-700 text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                <MessageCircle className="w-3 h-3" /> WhatsApp
              </span>
            )}
          </div>
          <button
            onClick={fetchNotificationHistory}
            className="text-xs text-amber-700 hover:text-amber-900 flex items-center gap-1"
          >
            <History className="w-3 h-3" /> View History
          </button>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <Wallet className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Outstanding</p>
              <p className="text-2xl font-bold text-gray-900">K{totalOutstanding.toLocaleString()}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Building2 className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Operators with Balance</p>
              <p className="text-2xl font-bold text-gray-900">{operatorsWithDebt}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Receipt className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Operators</p>
              <p className="text-2xl font-bold text-gray-900">{balances.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* View Tabs */}
      <div className="border-b">
        <div className="flex gap-4">
          <button
            onClick={() => setActiveView('balances')}
            className={`pb-3 px-1 border-b-2 font-medium text-sm ${
              activeView === 'balances'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            data-testid="balances-tab"
          >
            Operator Balances
          </button>
          <button
            onClick={() => setActiveView('ledger')}
            className={`pb-3 px-1 border-b-2 font-medium text-sm ${
              activeView === 'ledger'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            data-testid="ledger-tab"
          >
            Ledger Entries
          </button>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {/* Balances View */}
      {activeView === 'balances' && (
        <div className="bg-white rounded-xl border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="balances-table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Operator</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Commission %</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Due</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Paid</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Balance</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Pending</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-gray-500">Loading...</td>
                  </tr>
                ) : balances.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                      No commission data yet. Commissions are created when operators confirm cash payments.
                    </td>
                  </tr>
                ) : (
                  balances.map((op) => (
                    <tr key={op.operatorId} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{op.operatorName}</div>
                        <div className="text-xs text-gray-500">{op.contactEmail || 'No email'}</div>
                      </td>
                      <td className="px-4 py-3 text-right text-gray-900">{op.commissionRate}%</td>
                      <td className="px-4 py-3 text-right text-gray-900">K{op.totalDue.toLocaleString()}</td>
                      <td className="px-4 py-3 text-right text-green-600">K{op.totalPaid.toLocaleString()}</td>
                      <td className="px-4 py-3 text-right">
                        <span className={`font-semibold ${op.balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                          K{op.balance.toLocaleString()}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {op.pendingEntries > 0 && (
                          <span className="inline-flex items-center px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded-full">
                            {op.pendingEntries}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => {
                              setPaymentForm({ ...paymentForm, operatorId: op.operatorId });
                              setShowRecordPayment(true);
                            }}
                            className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200"
                            title="Record Payment"
                          >
                            <Banknote className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => generateStatement(op.operatorId)}
                            className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                            title="View Statement"
                          >
                            <FileText className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Ledger View */}
      {activeView === 'ledger' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="bg-white rounded-xl border p-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Operator</label>
                <select
                  value={filters.operatorId}
                  onChange={(e) => setFilters({ ...filters, operatorId: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2"
                >
                  <option value="">All Operators</option>
                  {balances.map((op) => (
                    <option key={op.operatorId} value={op.operatorId}>{op.operatorName}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2"
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="paid">Paid</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
                <input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
                <input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                onClick={fetchLedger}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                <Filter className="w-4 h-4" />
                Apply Filters
              </button>
            </div>
          </div>

          {/* Ledger Table */}
          <div className="bg-white rounded-xl border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="ledger-table">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Operator</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-gray-500">Loading...</td>
                    </tr>
                  ) : ledgerEntries.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                        No ledger entries found. Try adjusting filters.
                      </td>
                    </tr>
                  ) : (
                    ledgerEntries.map((entry) => (
                      <tr key={entry.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {new Date(entry.createdAt).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">{entry.operatorName}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            entry.type === 'commission_due' ? 'bg-red-100 text-red-700' :
                            entry.type === 'payment_received' ? 'bg-green-100 text-green-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {entry.type === 'commission_due' ? 'Commission Due' :
                             entry.type === 'payment_received' ? 'Payment' : 'Adjustment'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">{entry.description}</td>
                        <td className="px-4 py-3 text-right">
                          <span className={`font-medium ${
                            entry.type === 'payment_received' ? 'text-green-600' : 'text-gray-900'
                          }`}>
                            {entry.type === 'payment_received' ? '-' : '+'}K{entry.amount?.toLocaleString()}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {entry.status === 'pending' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded-full">
                              <Clock className="w-3 h-3" /> Pending
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                              <CheckCircle className="w-3 h-3" /> Paid
                            </span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Record Payment Modal */}
      {showRecordPayment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Record Payment</h3>
              <button onClick={() => setShowRecordPayment(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleRecordPayment} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Operator *</label>
                <select
                  value={paymentForm.operatorId}
                  onChange={(e) => setPaymentForm({ ...paymentForm, operatorId: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2"
                  required
                >
                  <option value="">Select Operator</option>
                  {balances.map((op) => (
                    <option key={op.operatorId} value={op.operatorId}>
                      {op.operatorName} (Owes K{op.balance.toLocaleString()})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount (K) *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                <select
                  value={paymentForm.paymentMethod}
                  onChange={(e) => setPaymentForm({ ...paymentForm, paymentMethod: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2"
                >
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="mobile_money">Mobile Money</option>
                  <option value="cash">Cash</option>
                  <option value="cheque">Cheque</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reference Number</label>
                <input
                  type="text"
                  value={paymentForm.reference}
                  onChange={(e) => setPaymentForm({ ...paymentForm, reference: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2"
                  placeholder="e.g., Bank transfer reference"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2"
                  rows={2}
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowRecordPayment(false)}
                  className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  data-testid="submit-payment-btn"
                >
                  Record Payment
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Adjustment Modal */}
      {showAdjustment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Create Adjustment</h3>
              <button onClick={() => setShowAdjustment(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleCreateAdjustment} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Operator *</label>
                <select
                  value={adjustmentForm.operatorId}
                  onChange={(e) => setAdjustmentForm({ ...adjustmentForm, operatorId: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2"
                  required
                >
                  <option value="">Select Operator</option>
                  {balances.map((op) => (
                    <option key={op.operatorId} value={op.operatorId}>{op.operatorName}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount (K) *</label>
                <input
                  type="number"
                  step="0.01"
                  value={adjustmentForm.amount}
                  onChange={(e) => setAdjustmentForm({ ...adjustmentForm, amount: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2"
                  required
                />
                <p className="mt-1 text-xs text-gray-500">Positive = operator owes more, Negative = credit to operator</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason *</label>
                <textarea
                  value={adjustmentForm.reason}
                  onChange={(e) => setAdjustmentForm({ ...adjustmentForm, reason: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2"
                  rows={3}
                  required
                  placeholder="e.g., Refund for cancelled booking, Promotional credit"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAdjustment(false)}
                  className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                  data-testid="submit-adjustment-btn"
                >
                  Create Adjustment
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Statement Modal */}
      {showStatement && statement && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 my-8 p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Commission Statement</h3>
              <button onClick={() => setShowStatement(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Statement Header */}
            <div className="border-b pb-4 mb-4">
              <div className="flex justify-between">
                <div>
                  <h4 className="font-semibold text-gray-900">{statement.operator?.name}</h4>
                  <p className="text-sm text-gray-500">Commission Rate: {statement.operator?.commissionRate}%</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500">Period</p>
                  <p className="font-medium">{statement.period?.from} to {statement.period?.to}</p>
                </div>
              </div>
            </div>
            
            {/* Summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500">Opening Balance</p>
                <p className="text-lg font-semibold">K{statement.summary?.openingBalance?.toLocaleString()}</p>
              </div>
              <div className="bg-red-50 rounded-lg p-3">
                <p className="text-xs text-gray-500">Commission Due</p>
                <p className="text-lg font-semibold text-red-600">+K{statement.summary?.commissionDue?.toLocaleString()}</p>
              </div>
              <div className="bg-green-50 rounded-lg p-3">
                <p className="text-xs text-gray-500">Payments</p>
                <p className="text-lg font-semibold text-green-600">-K{statement.summary?.paymentsReceived?.toLocaleString()}</p>
              </div>
              <div className="bg-indigo-50 rounded-lg p-3">
                <p className="text-xs text-gray-500">Closing Balance</p>
                <p className="text-lg font-semibold text-indigo-600">K{statement.summary?.closingBalance?.toLocaleString()}</p>
              </div>
            </div>
            
            {/* Cash Bookings Summary */}
            <div className="mb-6">
              <h5 className="font-medium text-gray-900 mb-2">Cash Bookings Summary</h5>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{statement.cashBookings?.count}</p>
                    <p className="text-xs text-gray-500">Bookings</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">K{statement.cashBookings?.totalRevenue?.toLocaleString()}</p>
                    <p className="text-xs text-gray-500">Total Revenue</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-red-600">K{statement.cashBookings?.totalCommission?.toLocaleString()}</p>
                    <p className="text-xs text-gray-500">Commission</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Transaction History */}
            {statement.entries?.length > 0 && (
              <div>
                <h5 className="font-medium text-gray-900 mb-2">Transaction History</h5>
                <div className="max-h-48 overflow-y-auto border rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left">Date</th>
                        <th className="px-3 py-2 text-left">Type</th>
                        <th className="px-3 py-2 text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {statement.entries.map((entry, idx) => (
                        <tr key={idx}>
                          <td className="px-3 py-2">{new Date(entry.createdAt).toLocaleDateString()}</td>
                          <td className="px-3 py-2">{entry.type}</td>
                          <td className="px-3 py-2 text-right">K{entry.amount?.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={downloadStatementPDF}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2"
                data-testid="download-statement-pdf-btn"
              >
                <Download className="w-4 h-4" />
                Download PDF
              </button>
              <button
                onClick={() => setShowStatement(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification Settings Modal */}
      {showNotificationSettings && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Bell className="w-5 h-5 text-amber-500" />
                Notification Settings
              </h3>
              <button onClick={() => setShowNotificationSettings(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Enable Toggle */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">Enable Auto-Reminders</p>
                  <p className="text-xs text-gray-500">Automatically email operators with outstanding balances</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notificationSettings.enabled}
                    onChange={(e) => setNotificationSettings({ ...notificationSettings, enabled: e.target.checked })}
                    className="sr-only peer"
                    data-testid="notification-toggle"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-amber-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500 pointer-events-none"></div>
                </label>
              </div>

              {/* Threshold Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Balance Threshold (K)</label>
                <input
                  type="number"
                  min="0"
                  step="100"
                  value={notificationSettings.thresholdAmount}
                  onChange={(e) => setNotificationSettings({ ...notificationSettings, thresholdAmount: parseFloat(e.target.value) || 0 })}
                  className="w-full border rounded-lg px-3 py-2"
                />
                <p className="mt-1 text-xs text-gray-500">Notify operators when balance exceeds this amount</p>
              </div>

              {/* Reminder Frequency */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reminder Frequency</label>
                <select
                  value={notificationSettings.reminderFrequency}
                  onChange={(e) => setNotificationSettings({ ...notificationSettings, reminderFrequency: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2"
                >
                  <option value="weekly">Weekly</option>
                  <option value="biweekly">Every 2 Weeks</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>

              {/* Email Subject */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Subject</label>
                <input
                  type="text"
                  value={notificationSettings.emailSubject}
                  onChange={(e) => setNotificationSettings({ ...notificationSettings, emailSubject: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>

              {/* Include Statement */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="includeStatement"
                  checked={notificationSettings.includeStatement}
                  onChange={(e) => setNotificationSettings({ ...notificationSettings, includeStatement: e.target.checked })}
                  className="rounded border-gray-300"
                />
                <label htmlFor="includeStatement" className="text-sm text-gray-700">
                  Include balance breakdown in email
                </label>
              </div>

              {/* SMS Section */}
              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium text-gray-900 flex items-center gap-2 mb-3">
                  <Smartphone className="w-4 h-4 text-green-600" />
                  SMS Notifications
                </h4>
                
                {/* SMS Enable Toggle */}
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg mb-3">
                  <div>
                    <p className="font-medium text-gray-900">Enable SMS Reminders</p>
                    <p className="text-xs text-gray-500">
                      {smsConfigured 
                        ? 'Send SMS to operators in addition to email' 
                        : 'SMS not configured - Twilio credentials required'}
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={notificationSettings.smsEnabled}
                      onChange={(e) => setNotificationSettings({ ...notificationSettings, smsEnabled: e.target.checked })}
                      className="sr-only peer"
                      disabled={!smsConfigured}
                      data-testid="sms-toggle"
                    />
                    <div className={`w-11 h-6 ${smsConfigured ? 'bg-gray-200' : 'bg-gray-100'} peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500 ${!smsConfigured ? 'opacity-50 cursor-not-allowed' : ''} pointer-events-none`}></div>
                  </label>
                </div>

                {/* SMS Message Template */}
                {notificationSettings.smsEnabled && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">SMS Message Template</label>
                    <textarea
                      value={notificationSettings.smsMessage}
                      onChange={(e) => setNotificationSettings({ ...notificationSettings, smsMessage: e.target.value })}
                      className="w-full border rounded-lg px-3 py-2 text-sm"
                      rows={3}
                      placeholder="Use {amount} for the balance amount"
                    />
                    <p className="mt-1 text-xs text-gray-500">Use {'{amount}'} to include the balance amount</p>
                  </div>
                )}
              </div>

              {/* WhatsApp Section */}
              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium text-gray-900 flex items-center gap-2 mb-3">
                  <MessageCircle className="w-4 h-4 text-emerald-600" />
                  WhatsApp Notifications
                </h4>
                
                {/* WhatsApp Enable Toggle */}
                <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg mb-3">
                  <div>
                    <p className="font-medium text-gray-900">Enable WhatsApp Reminders</p>
                    <p className="text-xs text-gray-500">
                      {whatsappConfigured 
                        ? 'Send WhatsApp messages to operators via Twilio' 
                        : 'WhatsApp not configured - TWILIO_WHATSAPP_NUMBER required in .env'}
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={notificationSettings.whatsappEnabled}
                      onChange={(e) => setNotificationSettings({ ...notificationSettings, whatsappEnabled: e.target.checked })}
                      className="sr-only peer"
                      disabled={!whatsappConfigured}
                      data-testid="whatsapp-toggle"
                    />
                    <div className={`w-11 h-6 ${whatsappConfigured ? 'bg-gray-200' : 'bg-gray-100'} peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500 ${!whatsappConfigured ? 'opacity-50 cursor-not-allowed' : ''} pointer-events-none`}></div>
                  </label>
                </div>

                {/* WhatsApp Message Template */}
                {notificationSettings.whatsappEnabled && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">WhatsApp Message Template</label>
                    <textarea
                      value={notificationSettings.whatsappMessage}
                      onChange={(e) => setNotificationSettings({ ...notificationSettings, whatsappMessage: e.target.value })}
                      className="w-full border rounded-lg px-3 py-2 text-sm"
                      rows={5}
                      placeholder="Use {amount} for the balance amount. Use *text* for bold."
                    />
                    <p className="mt-1 text-xs text-gray-500">Use {'{amount}'} for balance. WhatsApp formatting: *bold*, _italic_</p>
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowNotificationSettings(false)}
                  className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveNotificationSettings}
                  className="flex-1 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600"
                  data-testid="save-notification-settings-btn"
                >
                  Save Settings
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notification History Modal */}
      {showNotificationHistory && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <History className="w-5 h-5 text-indigo-500" />
                Notification History
              </h3>
              <button onClick={() => setShowNotificationHistory(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {notificationHistory.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Mail className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No notification emails have been sent yet</p>
              </div>
            ) : (
              <div className="max-h-96 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-2 text-left">Date</th>
                      <th className="px-4 py-2 text-left">Operator</th>
                      <th className="px-4 py-2 text-left">Email</th>
                      <th className="px-4 py-2 text-right">Balance</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {notificationHistory.map((notif, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-4 py-2">
                          {new Date(notif.sentAt).toLocaleDateString()}{' '}
                          <span className="text-xs text-gray-400">
                            {new Date(notif.sentAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </td>
                        <td className="px-4 py-2 font-medium">{notif.operatorName}</td>
                        <td className="px-4 py-2 text-gray-600">{notif.email}</td>
                        <td className="px-4 py-2 text-right text-red-600">K{notif.balanceAmount?.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setShowNotificationHistory(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Payment Modal */}
      {showBulkPayment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full mx-4 my-8 p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Users className="w-5 h-5 text-indigo-500" />
                Bulk Payment Recording
              </h3>
              <button onClick={() => setShowBulkPayment(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            {bulkPayments.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-300" />
                <p>No operators with outstanding balances</p>
              </div>
            ) : (
              <>
                <div className="mb-4 grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                    <select
                      value={bulkPaymentMethod}
                      onChange={(e) => setBulkPaymentMethod(e.target.value)}
                      className="w-full border rounded-lg px-3 py-2"
                    >
                      <option value="bank_transfer">Bank Transfer</option>
                      <option value="mobile_money">Mobile Money</option>
                      <option value="cash">Cash</option>
                      <option value="cheque">Cheque</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                    <input
                      type="text"
                      value={bulkPaymentNotes}
                      onChange={(e) => setBulkPaymentNotes(e.target.value)}
                      className="w-full border rounded-lg px-3 py-2"
                      placeholder="e.g., Monthly settlement"
                    />
                  </div>
                </div>

                <div className="max-h-64 overflow-y-auto border rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left w-10">
                          <input
                            type="checkbox"
                            onChange={(e) => {
                              setBulkPayments(bulkPayments.map(p => ({ ...p, selected: e.target.checked })));
                            }}
                            className="rounded"
                          />
                        </th>
                        <th className="px-3 py-2 text-left">Operator</th>
                        <th className="px-3 py-2 text-right">Balance</th>
                        <th className="px-3 py-2 text-right">Payment Amount</th>
                        <th className="px-3 py-2 text-left">Reference</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {bulkPayments.map((payment, idx) => (
                        <tr key={payment.operatorId} className={payment.selected ? 'bg-indigo-50' : ''}>
                          <td className="px-3 py-2">
                            <input
                              type="checkbox"
                              checked={payment.selected}
                              onChange={(e) => {
                                const updated = [...bulkPayments];
                                updated[idx].selected = e.target.checked;
                                setBulkPayments(updated);
                              }}
                              className="rounded"
                            />
                          </td>
                          <td className="px-3 py-2 font-medium">{payment.operatorName}</td>
                          <td className="px-3 py-2 text-right text-red-600">K{payment.balance.toLocaleString()}</td>
                          <td className="px-3 py-2 text-right">
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={payment.amount}
                              onChange={(e) => {
                                const updated = [...bulkPayments];
                                updated[idx].amount = e.target.value;
                                setBulkPayments(updated);
                              }}
                              className="w-24 border rounded px-2 py-1 text-right"
                              placeholder="0.00"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="text"
                              value={payment.reference}
                              onChange={(e) => {
                                const updated = [...bulkPayments];
                                updated[idx].reference = e.target.value;
                                setBulkPayments(updated);
                              }}
                              className="w-full border rounded px-2 py-1"
                              placeholder="Ref #"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="mt-4 flex justify-between items-center">
                  <div className="text-sm text-gray-500">
                    Selected: {bulkPayments.filter(p => p.selected).length} operators, 
                    Total: K{bulkPayments.filter(p => p.selected && p.amount).reduce((sum, p) => sum + parseFloat(p.amount || 0), 0).toLocaleString()}
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowBulkPayment(false)}
                      className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleBulkPaymentSubmit}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                      data-testid="submit-bulk-payment-btn"
                    >
                      Record Payments
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Balance Limit Settings Modal */}
      {showBalanceLimitSettings && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Shield className="w-5 h-5 text-red-500" />
                Balance Limit Settings
              </h3>
              <button onClick={() => setShowBalanceLimitSettings(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Enable Toggle */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">Enable Balance Limit</p>
                  <p className="text-xs text-gray-500">Restrict operators when commission balance exceeds limit</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={balanceLimitSettings.enabled}
                    onChange={(e) => setBalanceLimitSettings({ ...balanceLimitSettings, enabled: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-500 pointer-events-none"></div>
                </label>
              </div>

              {/* Limit Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Limit Amount (K)</label>
                <input
                  type="number"
                  min="0"
                  step="100"
                  value={balanceLimitSettings.limitAmount}
                  onChange={(e) => setBalanceLimitSettings({ ...balanceLimitSettings, limitAmount: parseFloat(e.target.value) || 0 })}
                  className="w-full border rounded-lg px-3 py-2"
                />
                <p className="mt-1 text-xs text-gray-500">Block operators when balance reaches this amount</p>
              </div>

              {/* Warning Threshold */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Warning Threshold (K)</label>
                <input
                  type="number"
                  min="0"
                  step="100"
                  value={balanceLimitSettings.warningThreshold}
                  onChange={(e) => setBalanceLimitSettings({ ...balanceLimitSettings, warningThreshold: parseFloat(e.target.value) || 0 })}
                  className="w-full border rounded-lg px-3 py-2"
                />
                <p className="mt-1 text-xs text-gray-500">Show warning when approaching limit</p>
              </div>

              {/* Block Bookings */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="blockBookings"
                  checked={balanceLimitSettings.blockNewBookings}
                  onChange={(e) => setBalanceLimitSettings({ ...balanceLimitSettings, blockNewBookings: e.target.checked })}
                  className="rounded border-gray-300"
                />
                <label htmlFor="blockBookings" className="text-sm text-gray-700">
                  Block new bookings when limit exceeded
                </label>
              </div>

              {/* Operators Over Limit */}
              {operatorsOverLimit.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm font-medium text-red-800 mb-2 flex items-center gap-2">
                    <Ban className="w-4 h-4" />
                    Operators Currently Over Limit
                  </p>
                  <div className="space-y-1">
                    {operatorsOverLimit.map(op => (
                      <div key={op.operatorId} className="flex justify-between text-sm">
                        <span className="text-red-700">{op.operatorName}</span>
                        <span className="font-medium text-red-800">K{op.balance.toLocaleString()} (+K{op.overBy.toLocaleString()})</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-4 border-t">
                <button
                  onClick={() => setShowBalanceLimitSettings(false)}
                  className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveBalanceLimitSettings}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                  data-testid="save-balance-limit-btn"
                >
                  Save Settings
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Plans Modal */}
      {showPaymentPlans && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full mx-4 my-8 p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <CalendarClock className="w-5 h-5 text-purple-500" />
                Payment Plans
              </h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleSendPlanReminders(false)}
                  disabled={sendingPlanReminders}
                  className="flex items-center gap-2 px-3 py-1.5 bg-amber-500 text-white rounded-lg hover:bg-amber-600 text-sm disabled:opacity-50"
                  data-testid="send-plan-reminders-btn"
                >
                  <Send className="w-4 h-4" />
                  {sendingPlanReminders ? 'Sending...' : 'Send Reminders'}
                </button>
                <button
                  onClick={() => setShowPlanReminderSettings(true)}
                  className="flex items-center gap-2 px-2 py-1.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm"
                  data-testid="plan-reminder-settings-btn"
                >
                  <Bell className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setShowCreatePlan(true)}
                  className="flex items-center gap-2 px-3 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm"
                >
                  <Plus className="w-4 h-4" />
                  Create Plan
                </button>
                <button onClick={() => setShowPaymentPlans(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Reminder Status Banner */}
            {planReminderSettings.enabled && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Bell className="w-4 h-4 text-amber-600" />
                  <span className="text-sm text-amber-800">
                    Auto-reminders enabled: {planReminderSettings.daysBeforeDue} days before due
                    {planReminderSettings.sendOverdueReminders && ` • Overdue: ${planReminderSettings.overdueReminderFrequency}`}
                  </span>
                </div>
                <button
                  onClick={fetchPlanReminderHistory}
                  className="text-xs text-amber-700 hover:text-amber-900 flex items-center gap-1"
                >
                  <History className="w-3 h-3" /> History
                </button>
              </div>
            )}

            {paymentPlans.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <CalendarClock className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No payment plans created yet</p>
                <button
                  onClick={() => setShowCreatePlan(true)}
                  className="mt-4 px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200"
                >
                  Create First Plan
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {paymentPlans.map(plan => (
                  <div key={plan.id} className={`border rounded-lg p-4 ${plan.status === 'active' ? 'border-purple-200 bg-purple-50/30' : 'border-gray-200'}`}>
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h4 className="font-semibold text-gray-900">{plan.operatorName}</h4>
                        <p className="text-sm text-gray-500">
                          {plan.numInstallments} {plan.frequency} installments • Created {new Date(plan.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          plan.status === 'active' ? 'bg-purple-100 text-purple-700' :
                          plan.status === 'completed' ? 'bg-green-100 text-green-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {plan.status}
                        </span>
                        {plan.status === 'active' && (
                          <button
                            onClick={() => handleCancelPlan(plan.id)}
                            className="text-xs text-red-600 hover:text-red-800"
                          >
                            Cancel
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="mb-3">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-600">Progress</span>
                        <span className="font-medium">K{plan.paidAmount?.toLocaleString()} / K{plan.totalAmount?.toLocaleString()}</span>
                      </div>
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-purple-500 rounded-full transition-all"
                          style={{ width: `${(plan.paidAmount / plan.totalAmount) * 100}%` }}
                        />
                      </div>
                    </div>

                    {/* Installments */}
                    {plan.status === 'active' && (
                      <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                        {plan.installments?.map((inst) => (
                          <div
                            key={inst.number}
                            className={`p-2 rounded-lg text-center text-xs ${
                              inst.status === 'paid' ? 'bg-green-100 text-green-700' :
                              inst.status === 'overdue' ? 'bg-red-100 text-red-700' :
                              'bg-gray-100 text-gray-700'
                            }`}
                          >
                            <p className="font-medium">#{inst.number}</p>
                            <p>K{inst.amount?.toLocaleString()}</p>
                            <p className="text-[10px] opacity-70">
                              {new Date(inst.dueDate).toLocaleDateString()}
                            </p>
                            {inst.status === 'pending' && (
                              <button
                                onClick={() => handlePayInstallment(plan.id, inst.number, inst.amount)}
                                className="mt-1 px-2 py-0.5 bg-purple-600 text-white rounded text-[10px]"
                              >
                                Pay
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Modal Footer */}
            <div className="flex justify-end gap-3 pt-4 mt-4 border-t">
              <button
                onClick={() => setShowPaymentPlans(false)}
                className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium"
                data-testid="payment-plans-done-btn"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Payment Plan Modal */}
      {showCreatePlan && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Create Payment Plan</h3>
              <button onClick={() => setShowCreatePlan(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Operator *</label>
                <select
                  value={newPlan.operatorId}
                  onChange={(e) => setNewPlan({ ...newPlan, operatorId: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2"
                  required
                >
                  <option value="">Select Operator</option>
                  {balances.filter(b => b.balance > 0).map((op) => (
                    <option key={op.operatorId} value={op.operatorId}>
                      {op.operatorName} (Owes K{op.balance.toLocaleString()})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Total Amount (K) *</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={newPlan.totalAmount}
                  onChange={(e) => setNewPlan({ ...newPlan, totalAmount: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Installments</label>
                  <select
                    value={newPlan.numInstallments}
                    onChange={(e) => setNewPlan({ ...newPlan, numInstallments: parseInt(e.target.value) })}
                    className="w-full border rounded-lg px-3 py-2"
                  >
                    {[2, 3, 4, 5, 6, 8, 10, 12].map(n => (
                      <option key={n} value={n}>{n} payments</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Frequency</label>
                  <select
                    value={newPlan.frequency}
                    onChange={(e) => setNewPlan({ ...newPlan, frequency: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2"
                  >
                    <option value="weekly">Weekly</option>
                    <option value="biweekly">Every 2 Weeks</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>
              </div>

              {newPlan.totalAmount && newPlan.numInstallments && (
                <div className="bg-purple-50 rounded-lg p-3 text-sm">
                  <p className="text-purple-800">
                    <strong>Preview:</strong> {newPlan.numInstallments} payments of ~K{(parseFloat(newPlan.totalAmount) / newPlan.numInstallments).toFixed(2)} each
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={newPlan.notes}
                  onChange={(e) => setNewPlan({ ...newPlan, notes: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2"
                  rows={2}
                  placeholder="Optional notes about this plan"
                />
              </div>

              <div className="flex gap-3 pt-4 border-t">
                <button
                  onClick={() => setShowCreatePlan(false)}
                  className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreatePaymentPlan}
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                  data-testid="create-plan-btn"
                >
                  Create Plan
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Plan Reminder Settings Modal */}
      {showPlanReminderSettings && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Bell className="w-5 h-5 text-amber-500" />
                Payment Plan Reminders
              </h3>
              <button onClick={() => setShowPlanReminderSettings(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Enable Toggle */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">Enable Auto-Reminders</p>
                  <p className="text-xs text-gray-500">Send email reminders for upcoming/overdue installments</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={planReminderSettings.enabled}
                    onChange={(e) => setPlanReminderSettings({ ...planReminderSettings, enabled: e.target.checked })}
                    className="sr-only peer"
                    data-testid="plan-reminder-toggle"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-amber-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500 pointer-events-none"></div>
                </label>
              </div>

              {/* Days Before Due */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Days Before Due Date</label>
                <select
                  value={planReminderSettings.daysBeforeDue}
                  onChange={(e) => setPlanReminderSettings({ ...planReminderSettings, daysBeforeDue: parseInt(e.target.value) })}
                  className="w-full border rounded-lg px-3 py-2"
                >
                  {[1, 2, 3, 5, 7, 10, 14].map(d => (
                    <option key={d} value={d}>{d} day{d > 1 ? 's' : ''} before</option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">Send upcoming reminder X days before due date</p>
              </div>

              {/* Overdue Reminders */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="sendOverdue"
                  checked={planReminderSettings.sendOverdueReminders}
                  onChange={(e) => setPlanReminderSettings({ ...planReminderSettings, sendOverdueReminders: e.target.checked })}
                  className="rounded border-gray-300"
                />
                <label htmlFor="sendOverdue" className="text-sm text-gray-700">
                  Send reminders for overdue installments
                </label>
              </div>

              {planReminderSettings.sendOverdueReminders && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Overdue Reminder Frequency</label>
                  <select
                    value={planReminderSettings.overdueReminderFrequency}
                    onChange={(e) => setPlanReminderSettings({ ...planReminderSettings, overdueReminderFrequency: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2"
                  >
                    <option value="daily">Daily</option>
                    <option value="every_3_days">Every 3 Days</option>
                    <option value="weekly">Weekly</option>
                  </select>
                </div>
              )}

              {/* Email Subjects */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Upcoming Reminder Subject</label>
                <input
                  type="text"
                  value={planReminderSettings.emailSubjectUpcoming}
                  onChange={(e) => setPlanReminderSettings({ ...planReminderSettings, emailSubjectUpcoming: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Overdue Reminder Subject</label>
                <input
                  type="text"
                  value={planReminderSettings.emailSubjectOverdue}
                  onChange={(e) => setPlanReminderSettings({ ...planReminderSettings, emailSubjectOverdue: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                />
              </div>

              <div className="flex gap-3 pt-4 border-t">
                <button
                  onClick={() => setShowPlanReminderSettings(false)}
                  className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSavePlanReminderSettings}
                  className="flex-1 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600"
                  data-testid="save-plan-reminder-settings-btn"
                >
                  Save Settings
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Plan Reminder History Modal */}
      {showPlanReminderHistory && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full mx-4 p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <History className="w-5 h-5 text-indigo-500" />
                Payment Plan Reminder History
              </h3>
              <button onClick={() => setShowPlanReminderHistory(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            {planReminderHistory.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Mail className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No payment plan reminders have been sent yet</p>
              </div>
            ) : (
              <div className="max-h-96 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-2 text-left">Date</th>
                      <th className="px-4 py-2 text-left">Operator</th>
                      <th className="px-4 py-2 text-center">Installment</th>
                      <th className="px-4 py-2 text-right">Amount</th>
                      <th className="px-4 py-2 text-center">Type</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {planReminderHistory.map((reminder, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-4 py-2">
                          {new Date(reminder.sentAt).toLocaleDateString()}{' '}
                          <span className="text-xs text-gray-400">
                            {new Date(reminder.sentAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </td>
                        <td className="px-4 py-2 font-medium">{reminder.operatorName}</td>
                        <td className="px-4 py-2 text-center">#{reminder.installmentNumber}</td>
                        <td className="px-4 py-2 text-right">K{reminder.amount?.toLocaleString()}</td>
                        <td className="px-4 py-2 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            reminder.type === 'overdue' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                          }`}>
                            {reminder.type === 'overdue' ? 'Overdue' : 'Upcoming'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setShowPlanReminderHistory(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
